import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  ToggleOn as ActivateIcon,
  ToggleOff as DeactivateIcon,
} from '@mui/icons-material';
import {
  getPromoCodes,
  createPromoCode,
  getPromoCodeStats,
  deactivatePromoCode,
  reactivatePromoCode,
  PromoCode,
  PromoCodeStats,
  CreatePromoCodeData,
} from '../adminApi';

type TypeFilter = 'all' | 'creator' | 'chef' | 'marketing' | 'beta';

const PromoCodeList: React.FC = () => {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [actionLoading, setActionLoading] = useState(false);

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newCode, setNewCode] = useState<CreatePromoCodeData>({
    code: '',
    code_type: 'marketing',
    trial_days: 30,
    max_uses: null,
    valid_until: null,
  });

  // Stats dialog
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedStats, setSelectedStats] = useState<PromoCodeStats | null>(null);
  const [selectedCode, setSelectedCode] = useState<PromoCode | null>(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPromoCodes({
        code_type: typeFilter === 'all' ? undefined : typeFilter,
      });
      setCodes(result || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = async () => {
    if (!newCode.code || !newCode.code_type) {
      setError('Code and type are required');
      return;
    }

    setActionLoading(true);
    setError(null);
    try {
      await createPromoCode(newCode);
      setSuccessMessage(`Promo code "${newCode.code}" created!`);
      setCreateDialogOpen(false);
      setNewCode({ code: '', code_type: 'marketing', trial_days: 30, max_uses: null, valid_until: null });
      fetchCodes();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create promo code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (code: PromoCode) => {
    setActionLoading(true);
    setError(null);
    try {
      if (code.is_active) {
        await deactivatePromoCode(code.code_id);
        setSuccessMessage(`"${code.code}" deactivated`);
      } else {
        await reactivatePromoCode(code.code_id);
        setSuccessMessage(`"${code.code}" reactivated`);
      }
      fetchCodes();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update promo code');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewStats = async (code: PromoCode) => {
    setSelectedCode(code);
    setStatsDialogOpen(true);
    setStatsLoading(true);
    try {
      const stats = await getPromoCodeStats(code.code_id);
      setSelectedStats(stats);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load stats');
      setStatsDialogOpen(false);
    } finally {
      setStatsLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'info' | 'default' => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'info' | 'default'> = {
      creator: 'primary',
      chef: 'secondary',
      marketing: 'success',
      beta: 'info',
    };
    return colors[type] || 'default';
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Promo Codes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Code
        </Button>
      </Stack>

      {/* Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={typeFilter}
          onChange={(_, v) => setTypeFilter(v)}
          sx={{ px: 2 }}
        >
          <Tab label="All" value="all" />
          <Tab label="Creator" value="creator" />
          <Tab label="Chef" value="chef" />
          <Tab label="Marketing" value="marketing" />
          <Tab label="Beta" value="beta" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Uses</TableCell>
              <TableCell>Trial Days</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : codes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  No promo codes found
                </TableCell>
              </TableRow>
            ) : (
              codes.map((code) => (
                <TableRow
                  key={code.code_id}
                  hover
                  onClick={() => handleViewStats(code)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Chip
                      label={code.code}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={code.code_type}
                      size="small"
                      color={getTypeColor(code.code_type)}
                    />
                  </TableCell>
                  <TableCell>
                    {code.creator_name || code.chef_name || '-'}
                  </TableCell>
                  <TableCell>
                    {code.uses_count}{code.max_uses ? ` / ${code.max_uses}` : ' / \u221e'}
                  </TableCell>
                  <TableCell>{code.trial_days}d</TableCell>
                  <TableCell>
                    <Chip
                      label={code.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={code.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(code.valid_until)}</TableCell>
                  <TableCell>{formatDate(code.created_at)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={code.is_active ? 'Deactivate' : 'Reactivate'}>
                      <IconButton
                        size="small"
                        color={code.is_active ? 'warning' : 'success'}
                        onClick={() => handleToggleActive(code)}
                        disabled={actionLoading}
                      >
                        {code.is_active ? <DeactivateIcon fontSize="small" /> : <ActivateIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Code Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Promo Code</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Code"
              fullWidth
              required
              value={newCode.code}
              onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
              placeholder="e.g., SUMMER2026"
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
            <FormControl fullWidth required>
              <InputLabel>Type</InputLabel>
              <Select
                value={newCode.code_type}
                label="Type"
                onChange={(e) => setNewCode({ ...newCode, code_type: e.target.value as CreatePromoCodeData['code_type'] })}
              >
                <MenuItem value="marketing">Marketing</MenuItem>
                <MenuItem value="beta">Beta</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Trial Days"
              type="number"
              fullWidth
              value={newCode.trial_days ?? ''}
              onChange={(e) => setNewCode({ ...newCode, trial_days: parseInt(e.target.value) || 0 })}
            />
            <TextField
              label="Max Uses (leave empty for unlimited)"
              type="number"
              fullWidth
              value={newCode.max_uses ?? ''}
              onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value ? parseInt(e.target.value) : null })}
            />
            <TextField
              label="Valid Until"
              type="date"
              fullWidth
              value={newCode.valid_until ?? ''}
              onChange={(e) => setNewCode({ ...newCode, valid_until: e.target.value || null })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={actionLoading || !newCode.code}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stats Dialog */}
      <Dialog open={statsDialogOpen} onClose={() => setStatsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Stats: {selectedCode?.code}
        </DialogTitle>
        <DialogContent>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedStats ? (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Referrals
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography variant="h5">{selectedStats.total_referrals}</Typography>
                    <Typography variant="caption">Total</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" color="success.main">{selectedStats.active_referrals}</Typography>
                    <Typography variant="caption">Active</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" color="info.main">{selectedStats.trialing_referrals}</Typography>
                    <Typography variant="caption">Trialing</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" color="error.main">{selectedStats.churned_referrals}</Typography>
                    <Typography variant="caption">Churned</Typography>
                  </Box>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Revenue
                </Typography>
                <Stack direction="row" spacing={3}>
                  <Box>
                    <Typography variant="h5">${Number(selectedStats.total_revenue).toFixed(2)}</Typography>
                    <Typography variant="caption">Total Revenue</Typography>
                  </Box>
                  <Box>
                    <Typography variant="h5" color="success.main">${Number(selectedStats.total_commission).toFixed(2)}</Typography>
                    <Typography variant="caption">Total Commission</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromoCodeList;
